import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import JaceLayout from "./JaceLayout";
import { JACE } from "./shared";

const uid = () => `q_${Math.random().toString(36).slice(2, 10)}`;

export default function JaceFeedbackSettings() {
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/jace/feedback/settings").then(({ data }) => setSettings(data)).catch(() => setSettings(false));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/jace/feedback/settings", { questions: settings.questions, templates: settings.templates });
      toast.success("Feedback settings opgeslagen");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Opslaan mislukt"); }
    setBusy(false);
  };

  if (!settings) return <JaceLayout><div className="chip text-[#A3A3A3]">Laden…</div></JaceLayout>;

  const patchQ = (i, patch) => setSettings((p) => ({ ...p, questions: p.questions.map((q, j) => j === i ? { ...q, ...patch } : q) }));
  const patchT = (i, patch) => setSettings((p) => ({ ...p, templates: p.templates.map((t, j) => j === i ? { ...t, ...patch } : t) }));

  return (
    <JaceLayout>
      <div className="max-w-4xl" data-testid="jace-feedback-settings">
        <div className="section-tag section-tag-dark mb-3">// FEEDBACK SETTINGS</div>
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <h2 className="font-display uppercase text-4xl md:text-6xl">Vragen & templates.</h2>
            <p className="text-sm text-[#A3A3A3] mt-2">Alle vragen zijn optioneel. Alleen expliciet geselecteerde antwoorden mogen in een atletrapport.</p>
          </div>
          <button onClick={save} disabled={busy} className="chip px-4 py-2.5 inline-flex items-center gap-2" style={{ background: JACE, color: "#0A0A0A" }} data-testid="jace-feedback-settings-save"><Save size={13}/> {busy ? "OPSLAGEN…" : "SAVE"}</button>
        </div>

        <div className="border border-[#F5F3F0]/15 p-5 mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="chip" style={{ color: JACE }}>CUSTOM QUESTIONS</div>
            <button onClick={() => setSettings((p) => ({ ...p, questions: [...p.questions, { id: uid(), label: "", type: "text", shareable: true, active: true }] }))} className="chip px-3 py-2 border border-[#F5F3F0]/25 inline-flex items-center gap-1.5" data-testid="jace-feedback-add-question"><Plus size={12}/> VRAAG</button>
          </div>
          <div className="space-y-3">
            {settings.questions.map((q, i) => (
              <div key={`${q.id}-${i}`} className="border border-[#F5F3F0]/10 p-4 grid md:grid-cols-[1fr_140px_110px_100px_40px] gap-3 items-center" data-testid={`jace-feedback-question-${q.id}`}>
                <input value={q.label} onChange={(e) => patchQ(i, { label: e.target.value })} placeholder="Vraaglabel" className="editorial-input dark" data-testid={`jace-feedback-question-label-${q.id}`}/>
                <select value={q.type} onChange={(e) => patchQ(i, { type: e.target.value })} className="editorial-input dark" data-testid={`jace-feedback-question-type-${q.id}`}>
                  <option value="text" className="bg-[#0A0A0A]">Vrije tekst</option>
                  <option value="rating" className="bg-[#0A0A0A]">Beoordeling</option>
                  <option value="block" className="bg-[#0A0A0A]">Per onderdeel</option>
                </select>
                <label className="chip text-[#A3A3A3] inline-flex items-center gap-2"><input type="checkbox" checked={!!q.shareable} onChange={(e) => patchQ(i, { shareable: e.target.checked })} className="accent-[#C6FF3C]"/> Deelbaar</label>
                <label className="chip text-[#A3A3A3] inline-flex items-center gap-2"><input type="checkbox" checked={!!q.active} onChange={(e) => patchQ(i, { active: e.target.checked })} className="accent-[#C6FF3C]"/> Actief</label>
                <button onClick={() => setSettings((p) => ({ ...p, questions: p.questions.filter((_, j) => j !== i) }))} className="text-[#F87171]" data-testid={`jace-feedback-question-delete-${q.id}`}><Trash2 size={15}/></button>
              </div>
            ))}
          </div>
        </div>

        <div className="border border-[#F5F3F0]/15 p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="chip" style={{ color: JACE }}>REPORT TEMPLATES</div>
            <button onClick={() => setSettings((p) => ({ ...p, templates: [...p.templates, { id: `tpl_${Math.random().toString(36).slice(2, 8)}`, name: "", guidance: "" }] }))} className="chip px-3 py-2 border border-[#F5F3F0]/25 inline-flex items-center gap-1.5" data-testid="jace-feedback-add-template"><Plus size={12}/> TEMPLATE</button>
          </div>
          <div className="space-y-3">
            {settings.templates.map((t, i) => (
              <div key={`${t.id}-${i}`} className="border border-[#F5F3F0]/10 p-4" data-testid={`jace-feedback-template-${t.id}`}>
                <div className="flex gap-3 items-center mb-3">
                  <input value={t.name} onChange={(e) => patchT(i, { name: e.target.value })} placeholder="Template naam" className="editorial-input dark flex-1" data-testid={`jace-feedback-template-name-${t.id}`}/>
                  <button onClick={() => setSettings((p) => ({ ...p, templates: p.templates.filter((_, j) => j !== i) }))} className="text-[#F87171]" data-testid={`jace-feedback-template-delete-${t.id}`}><Trash2 size={15}/></button>
                </div>
                <textarea rows={3} value={t.guidance} onChange={(e) => patchT(i, { guidance: e.target.value })} placeholder="Schrijfstijl en richtlijnen voor JACE…" className="editorial-input dark w-full" data-testid={`jace-feedback-template-guidance-${t.id}`}/>
              </div>
            ))}
          </div>
        </div>
        <Link to="/jace/feedback" className="chip text-[#A3A3A3] inline-block mt-6" data-testid="jace-feedback-settings-back">← TERUG NAAR FEEDBACK</Link>
      </div>
    </JaceLayout>
  );
}
